import { useCallback, useEffect, useState } from 'react'
import { toursService } from '../services/tours.service'
import { toursLiveActivity } from '../tours/live-activity'
import type { TourTripSnapshot } from '../types/tours.types'

async function registerPushTokenWithRetry(
  bookingId: string,
  activityId: string,
  pushToken: string,
): Promise<void> {
  let lastError: unknown
  for (const delayMs of [0, 1_000, 3_000]) {
    if (delayMs) await new Promise((resolve) => setTimeout(resolve, delayMs))
    try {
      await toursService.registerLiveActivity(bookingId, activityId, pushToken)
      return
    } catch (error) {
      lastError = error
    }
  }
  throw lastError
}

export function useTourLiveActivity(snapshot: TourTripSnapshot | null) {
  const bookingId = snapshot?.booking.id ?? ''
  const isTerminal = ['completed', 'cancelled'].includes(snapshot?.run?.status ?? '')
  const [available, setAvailable] = useState(false)
  const [activityId, setActivityId] = useState<string | null>(null)
  const [isPending, setIsPending] = useState(false)

  useEffect(() => {
    let active = true
    void (async () => {
      const enabled = await toursLiveActivity.isAvailable()
      if (!active) return
      setAvailable(enabled)
      if (!enabled || !bookingId) return

      const registration = await toursLiveActivity.activityForBooking(bookingId)
      if (!active) return
      setActivityId(registration?.activityId ?? null)
      if (registration?.pushToken) {
        void registerPushTokenWithRetry(
          bookingId,
          registration.activityId,
          registration.pushToken,
        ).catch(() => undefined)
      }
    })().catch(() => {
      if (active) {
        setAvailable(false)
        setActivityId(null)
      }
    })
    return () => {
      active = false
    }
  }, [bookingId])

  useEffect(() => {
    if (!available || !bookingId) return undefined
    if (!activityId) return undefined
    return toursLiveActivity.addPushTokenListener(bookingId, activityId, (registration) => {
      if (registration.bookingId !== bookingId || !registration.pushToken) return
      setActivityId(registration.activityId)
      void registerPushTokenWithRetry(
        bookingId,
        registration.activityId,
        registration.pushToken,
      ).catch(() => undefined)
    })
  }, [activityId, available, bookingId])

  useEffect(() => {
    if (!snapshot || !activityId) return
    let active = true
    const finished = ['completed', 'cancelled'].includes(snapshot.run?.status ?? '')
    const operation = finished
      ? toursLiveActivity.end(activityId, snapshot)
      : toursLiveActivity.update(activityId, snapshot)
    void operation
      .then(() => {
        if (active && finished) setActivityId(null)
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [activityId, snapshot])

  const activate = useCallback(async () => {
    if (!snapshot || !available || activityId) return
    setIsPending(true)
    try {
      const registration = await toursLiveActivity.start(snapshot)
      setActivityId(registration.activityId)
      if (registration.pushToken) {
        void registerPushTokenWithRetry(
          snapshot.booking.id,
          registration.activityId,
          registration.pushToken,
        ).catch(() => undefined)
      }
    } finally {
      setIsPending(false)
    }
  }, [activityId, available, snapshot])

  return { available: available && !isTerminal, active: activityId != null, isPending, activate }
}
